import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { connect } from 'react-redux';
import { useLocale as use_locale } from '../../reducers/locale';
import { withGrowl } from '../../components/GrowlNotifications';
import { intlShape, historyShape, growlShape } from '../../components/prop-types';
import PageNotFound from '../../components/PageNotFound';
import { Loading } from '../../components/Loading';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { InputTextWithValidationMessage, DropdownWithValidation } from '../../components/prime-formik';
import { saveViewerLocale } from '../../services/locale/ViewerLocale';
import { compare } from '../../utils/AppUtil';

const messages = defineMessages({
  firstName: { id: 'register.first_name', defaultMessage: 'First name' },
  lastName: { id: 'register.last_name', defaultMessage: 'Last name' },
  email: { id: 'register.email', defaultMessage: 'Email' },
  locale: { id: 'profile.locale', defaultMessage: 'Preferred language' },
  country: { id: 'attrs.country', defaultMessage: 'Country' },
  timeZone: { id: 'profile.timezone', defaultMessage: 'Time zone' },
  multiFactorAuthentication: { id: 'profile.multiFactorAuthentication', defaultMessage: 'Multi factor authentication' },
  updateDetails: { id: 'user.update_details', defaultMessage: 'Update details' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
});

const mapState = (state) => {
  const { cldr } = state.i18n;
  return {
    cldr,
  };
};

const mapDispatchToProps = dispatch => ({
  updateMessages: selectedLanguage => dispatch(use_locale(selectedLanguage)),
});

const UPDATE_DETAILS = gql`
  mutation updateUserDetails($input: UpdateDetailsInput!) {
    updateDetails(input: $input) {
      id,
      firstName,
      lastName,
      email,
      locale,
      country,
      timeZone
    }
  }
`;

const USER_QUERY = gql`
  query getUserById($accountId: ID!, $userId: ID!, $first: Int) {
    account(id: $accountId) {
      id
      user(id: $userId) {
        id,
        firstName,
        lastName,
        email,
        country,
        createdOn,
        locale,
        timeZone,
        enabled,
        emailVerified,
        roles {
            account {
                id
            },
            role {
              id
              name
              displayName
              description
            }
        },
        accounts(first: $first) {
          edges {
            node {
              id
            }
            agreedToBeContactedByGpi,
            gpiContactAgreementUpdatedOn
            agreedToBeContactedByGpiReseller
            gpiResellerContactAgreementUpdatedOn
            agreedToBeContactedByGpiPartners
            gpiPartnersContactAgreementUpdatedOn
          }
        }
      }
    }

    locales: activeLocales
    countries: availableCountries
    timeZones: availableTimeZones
  }
`;

function UpdateUserDetailsForm(props) {
  const {
    accountId,
    userId,
    intl: { formatMessage },
    data,
    cldr,
    toggleUpdateDetails,
    availableMultiFactorAuthenticationOptions,
  } = props;

  const { localeDisplayName } = cldr;

  const { locales, countries, timeZones } = data;

  const supportedLanguages = locales.map(
    lang => ({
      value: lang,
      label: localeDisplayName(lang),
    }),
  );

  const availableTimeZones = timeZones.map(
    timeZone => ({
      value: timeZone,
      label: timeZone,
    }),
  );

  const sortedLanguages = supportedLanguages.sort(compare);

  const sortedTimeZones = availableTimeZones.sort(compare);

  const countryOptions = cldr.teritoryDisplayNames(countries);

  const multiFactorAuthenticationOptions = availableMultiFactorAuthenticationOptions.map(option => ({
    value: option,
    label: option,
  }));

  return (
    <div className="p-grid">
      <div className="p-sm-12 p-md-6">
        <Form>
          <input type="hidden" name="accountId" value={accountId} />
          <input type="hidden" name="userId" value={userId} />
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <Field
                id="firstName"
                name="firstName"
                type="text"
                labelText={formatMessage(messages.firstName)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="lastName"
                name="lastName"
                type="text"
                labelText={formatMessage(messages.lastName)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="email"
                name="email"
                type="text"
                labelText={formatMessage(messages.email)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <label htmlFor="locale" className="dropdown-label">{formatMessage(messages.locale)}</label>
              <Field
                id="locale"
                name="locale"
                component={DropdownWithValidation}
                filter
                filterBy="label,value"
                options={sortedLanguages}
              />
            </div>
            <div className="p-col-12">
              <label htmlFor="country" className="dropdown-label">{formatMessage(messages.country)}</label>
              <Field
                id="country"
                name="country"
                component={DropdownWithValidation}
                filter
                filterBy="label,value"
                options={countryOptions}
              />
            </div>
            <div className="p-col-12">
              <label htmlFor="timeZone" className="dropdown-label">{formatMessage(messages.timeZone)}</label>
              <Field
                id="timeZone"
                name="timeZone"
                component={DropdownWithValidation}
                filter
                filterBy="label,value"
                options={sortedTimeZones}
              />
            </div>
            <div className="p-col-12">
              <label
                htmlFor="timeZone"
                className="dropdown-label"
              >
                {formatMessage(messages.multiFactorAuthentication)}
              </label>
              <Field
                id="multiFactorAuthenticationOption"
                name="multiFactorAuthenticationOption"
                component={DropdownWithValidation}
                showClear
                filter
                filterBy="label,value"
                options={multiFactorAuthenticationOptions}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Button
                type="button"
                className="p-button-secondary"
                label={formatMessage(messages.cancel)}
                onClick={() => toggleUpdateDetails(false)}
              />
              <Button type="submit" label={formatMessage(messages.submit)} />
            </div>
          </div>
          <Debug />
        </Form>
      </div>
    </div>
  );
}

UpdateUserDetailsForm.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  cldr: PropTypes.objectOf(PropTypes.any).isRequired,
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  toggleUpdateDetails: PropTypes.func.isRequired,
  availableMultiFactorAuthenticationOptions: PropTypes.arrayOf(PropTypes.any).isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    updateUserDetailsMutation,
    growl,
    intl: { formatMessage },
    toggleUpdateDetails,
    updateMessages,
  } = props;

  const {
    accountId,
    userId,
    firstName,
    lastName,
    email,
    locale,
    country,
    timeZone,
    multiFactorAuthenticationOption,
  } = values;

  try {
    await updateUserDetailsMutation({
      variables: {
        input: {
          accountId,
          userId,
          firstName,
          lastName,
          email,
          locale,
          country,
          timeZone,
          multiFactorAuthenticationOption,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
    updateMessages(locale);
    saveViewerLocale(locale);
    toggleUpdateDetails(false);
  } catch (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }
}

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    accountId: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    userId: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    firstName: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    lastName: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    email: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    locale: Yup.string()
      .default(''),
    country: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    timeZone: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
  });
}

function mapPropsToValues({
  intl: { formatMessage }, accountId, userId, data,
  currentMultiFactorAuthenticationOption,
}) {
  const validationSchema = buildValidationSchema(formatMessage);

  const initialValues = {
    ...validationSchema.cast(),
    accountId,
    userId,
    firstName: data.account.user.firstName,
    lastName: data.account.user.lastName,
    email: data.account.user.email,
    locale: data.account.user.locale,
    country: data.account.user.country,
    timeZone: data.account.user.timeZone,
    multiFactorAuthenticationOption: currentMultiFactorAuthenticationOption,
  };
  return initialValues;
}

const UpdateUserDetailsEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(UpdateUserDetailsForm);

function UpdateUserDetails(props) {
  const {
    intl: { formatMessage },
    growl,
    accountId,
    userId,
  } = props;

  const { loading, error, data } = useQuery(USER_QUERY, {
    variables: {
      accountId,
      userId,
      first: 1,
    },
  });

  const [updateUserDetailsMutation] = useMutation(UPDATE_DETAILS, {
    refetchQueries: ['getUserById'],
  });

  if (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }

  if (loading) {
    return <Loading />;
  }

  if (data == null && data.account == null) {
    return <PageNotFound />;
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel header={formatMessage(messages.updateDetails)}>
          <UpdateUserDetailsEnhancedForm
            updateUserDetailsMutation={updateUserDetailsMutation}
            data={data}
            {...props}
          />
        </Panel>
      </div>
    </div>
  );
}

UpdateUserDetails.propTypes = {
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
};

export default injectIntl(withRouter(connect(mapState, mapDispatchToProps)(withGrowl(UpdateUserDetails))));
